import React, { useEffect, useRef, useState } from "react";

/**
 * ModalZoomPan
 * - Pure pointer-based pan/zoom (no library)
 * - Fit-to-container on mount, robust against passive listeners
 * - Wheel zoom around cursor, pointer capture for drag
 * - Markers render INSIDE the transformed layer to stay aligned
 */
export default function ModalZoomPan({
  src,
  points = [],
  onPointSelect = () => {},
  headerOffsetPx = 70, // sticky header height inside the modal
}) {
  const viewRef = useRef(null);  // gesture surface (fills modal body)
  const layerRef = useRef(null); // transformed layer (img + markers)
  const imgRef  = useRef(null);

  // transform state
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // natural image size
  const naturalRef = useRef({ w: 0, h: 0 });

  // drag state
  const drag = useRef({ active: false, startX: 0, startY: 0, startTx: 0, startTy: 0 });

  const applyTransform = (nx, ny, ns) => {
    setScale(ns);
    setTx(nx);
    setTy(ny);
    // no console spam here; uncomment if needed:
    // console.log("[transform]", { nx, ny, ns });
  };

  // Fit image to view (contain)
  const fit = () => {
    const view = viewRef.current;
    const img  = imgRef.current;
    if (!view || !img) return;

    const W = view.clientWidth;
    const H = view.clientHeight;
    const iw = img.naturalWidth  || img.width;
    const ih = img.naturalHeight || img.height;

    if (!W || !H || !iw || !ih) return;

    const s = Math.min(W / iw, H / ih);
    const x = (W - iw * s) / 2;
    const y = (H - ih * s) / 2;

    console.log("[fit] set", { x, y, s, W, H, iw, ih });
    applyTransform(x, y, s);
  };

  // INIT: measure image, then fit; refit on container resize
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    // ResizeObserver keeps fit correct when modal resizes
    const ro = new ResizeObserver(() => {
      if (!drag.current.active) fit();
    });
    ro.observe(view);

    const img = imgRef.current;
    const onload = () => {
      naturalRef.current = {
        w: img.naturalWidth || 0,
        h: img.naturalHeight || 0,
      };
      // fit next frame to ensure measured dimensions are final
      requestAnimationFrame(fit);
    };
    if (img && !img.complete) img.addEventListener("load", onload, { once: true });
    else requestAnimationFrame(fit);

    return () => {
      ro.disconnect();
      if (img) img.removeEventListener("load", onload);
    };
  }, []);

  // ----- Pointer Events: drag pan -----
  const onPointerDown = (e) => {
    const view = viewRef.current;
    if (!view) return;
    if (e.button !== undefined && e.button !== 0) return; // left button only for mouse

    view.setPointerCapture?.(e.pointerId);
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startTx: tx,
      startTy: ty,
    };
    // important to block passive scroll/zoomers
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    // block any passive handlers
    e.preventDefault();

    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    applyTransform(drag.current.startTx + dx, drag.current.startTy + dy, scale);
  };

  const onPointerUp = (e) => {
    const view = viewRef.current;
    drag.current.active = false;
    view?.releasePointerCapture?.(e.pointerId);
  };

  // ----- Wheel zoom (around cursor) with passive=false -----
  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    const onWheel = (e) => {
      // preventDefault works because this listener is non-passive
      e.preventDefault();

      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      const direction = Math.sign(e.deltaY); // +1 out, -1 in
      const factor = 0.2;
      const newScale = Math.max(0.05, Math.min(8, scale * (1 - direction * factor)));

      // keep world point under cursor
      const wx = (cx - tx) / scale;
      const wy = (cy - ty) / scale;

      const nx = cx - wx * newScale;
      const ny = cy - wy * newScale;

      // console.log("[wheel]", { scale, newScale, cx, cy, wx, wy, nx, ny });
      applyTransform(nx, ny, newScale);
    };

    // Attach a REAL non-passive listener to beat passive warnings
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel, { passive: false });
  }, [scale, tx, ty]);

  return (
    <div
      ref={viewRef}
      className="w-full h-full overflow-hidden relative"
      style={{ touchAction: "none", cursor: drag.current.active ? "grabbing" : "grab" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        ref={layerRef}
        className="absolute top-0 left-0 will-change-transform"
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: "top left" }}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Diagram"
          draggable={false}
          className="block max-w-none select-none"
          onDragStart={(e) => e.preventDefault()}
        />
        {/* Markers (absolute in content space) */}
        {points
          .filter((p) => !p.is_hidden)
          .map((p, i) => (
            <div
              key={i}
              className="absolute cursor-pointer"
              onClick={() => onPointSelect(p)}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                className="rounded-full border-2 flex items-center justify-center text-black font-bold border-green-500 bg-white/80"
                style={{ width: `${p.size}px`, height: `${p.size}px` }}
              >
                {p.number}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
